"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useRef, useState } from "react"
import type {
  OnboardingStateResponse,
  PersonalInfoPatch,
  ProfileResponse,
} from "@bitebalance/contracts"
import { PersonalInfoPatchSchema } from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { MoreSubpageHeader } from "@/components/layout/MoreSubpageHeader"
import { LoadingState, ErrorState } from "@/components/state"
import { Field } from "@/components/onboarding/Field"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { OptionGroup } from "@/components/onboarding/OptionGroup"
import { PlanSyncBanner, usePlanSync } from "@/components/plan/PlanSyncBanner"
import { formatKcal, useI18n } from "@/i18n"
import { avatarInitials, processAvatarFile } from "@/lib/avatarImage"

/**
 * /app/more/profile — the account area (2026-08 profile batch):
 *  - "Personal information" card: avatar (initials fallback), first/family
 *    name, date of birth, read-only email (Cognito sign-in alias), optional
 *    phone. Saved via `PATCH /v1/profile { personalInfo }`. Existing users
 *    with a NULL record get a non-blocking "complete your profile" banner —
 *    never a gate, never re-onboarding.
 *  - "Body data" card (owner request 2026-08-28): sex, age, height, weight
 *    and target weight — the inputs that drive the calorie target — edited
 *    via the canonical step-4 PUT (`PUT /v1/onboarding/4`), which recomputes
 *    targets server-side (ADR-027). ADR-037: the running week is NEVER
 *    auto-rebuilt by a body edit — the user sees the truthful target move,
 *    and a >=50 kcal shift offers an explicit "Rebuild this week".
 * Preference edits live on /app/more/preferences; goal editing on
 * /app/more/goals; hard constraints on the dedicated ConstraintsPage.
 *
 * Photo flow (ADR-024): client downscales to a ≤512px square JPEG
 * (lib/avatarImage.ts), POST /v1/profile/photo-upload → presigned PUT →
 * PATCH { personalInfo: { photoKey } } confirms; photoKey:null removes.
 */

interface PersonalDraft {
  firstName: string
  familyName: string
  dateOfBirth: string
  phone: string
}

interface BodyDraft {
  sex: "female" | "male" | null
  age: string
  heightCm: string
  weightKg: string
  targetWeightKg: string
}

/** Step-4 stored answer, as re-parsed by GET /v1/onboarding. */
interface Step4Answer {
  sex: "female" | "male" | null
  age: number | null
  heightCm: number | null
  weightKg: number | null
  targetWeightKg: number | null
  preferNotToAnswer: string[]
  activity?: unknown
}

function numOrNull(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === "") return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : null
}

/** Mirrors the Step4Schema bounds for inline feedback; the server re-validates. */
function bodyDraftInvalid(draft: BodyDraft): boolean {
  const age = numOrNull(draft.age)
  const height = numOrNull(draft.heightCm)
  const weight = numOrNull(draft.weightKg)
  const target = numOrNull(draft.targetWeightKg)
  if (draft.sex === null || age === null || height === null || weight === null) return true
  if (age < 18 || age > 100 || !Number.isInteger(age)) return true
  if (height < 120 || height > 230) return true
  if (weight < 30 || weight > 300) return true
  if (draft.targetWeightKg.trim() !== "" && (target === null || target < 30 || target > 300))
    return true
  return false
}

function personalDraftFrom(profile: ProfileResponse): PersonalDraft {
  return {
    firstName: profile.personalInfo.firstName ?? "",
    familyName: profile.personalInfo.familyName ?? "",
    dateOfBirth: profile.personalInfo.dateOfBirth ?? "",
    phone: profile.personalInfo.phone ?? "",
  }
}

/** Builds the PATCH personalInfo payload from the draft, or null if nothing changed. */
function personalPatchFrom(
  profile: ProfileResponse,
  draft: PersonalDraft
): PersonalInfoPatch | null {
  const patch: PersonalInfoPatch = {}
  const p = profile.personalInfo
  if (draft.firstName.trim() && draft.firstName.trim() !== (p.firstName ?? ""))
    patch.firstName = draft.firstName.trim()
  if (draft.familyName.trim() && draft.familyName.trim() !== (p.familyName ?? ""))
    patch.familyName = draft.familyName.trim()
  if (draft.dateOfBirth && draft.dateOfBirth !== (p.dateOfBirth ?? ""))
    patch.dateOfBirth = draft.dateOfBirth
  const phone = draft.phone.trim()
  if (phone !== (p.phone ?? "")) patch.phone = phone === "" ? null : phone
  return Object.keys(patch).length > 0 ? patch : null
}

/** ADR-037: same threshold as Amy's chat rule — below this a target move is noise. */
const REBUILD_OFFER_THRESHOLD_KCAL = 50

export default function ProfilePage() {
  const api = useApiClient()
  const { t, locale } = useI18n()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [state, setState] = useState<"loading" | "ready" | "error">("loading")
  const [personalDraft, setPersonalDraft] = useState<PersonalDraft>({
    firstName: "",
    familyName: "",
    dateOfBirth: "",
    phone: "",
  })
  const [personalState, setPersonalState] = useState<"idle" | "saving" | "saved" | "error">("idle")
  const [photoState, setPhotoState] = useState<"idle" | "uploading" | "error">("idle")
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [step4, setStep4] = useState<Step4Answer | null>(null)
  const [bodyDraft, setBodyDraft] = useState<BodyDraft | null>(null)
  const [bodyState, setBodyState] = useState<"idle" | "saving" | "saved" | "error">("idle")
  // ADR-037: a body-data save recomputes the target but NEVER rebuilds the
  // running week (owner decision 2026-08-30 — a Monday weigh-in must not
  // invalidate groceries already bought). The user sees the truthful
  // outcome; a >=50 kcal shift additionally OFFERS an explicit rebuild.
  const [bodyNotice, setBodyNotice] = useState<{ fromKcal: number; toKcal: number } | null>(null)
  const { state: planSync, start: startPlanSync } = usePlanSync()

  useEffect(() => {
    let cancelled = false
    Promise.all([
      api.getProfile(),
      // Body data lives in the stored step-4 answer; a failure degrades the
      // body card away instead of blocking the page.
      api.getOnboarding().catch(() => null),
    ])
      .then(([full, ob]: [ProfileResponse, OnboardingStateResponse | null]) => {
        if (cancelled) return
        setProfile(full)
        setPersonalDraft(personalDraftFrom(full))
        const answer = (ob?.answers.find(a => a.step === 4)?.answer ?? null) as Step4Answer | null
        setStep4(answer)
        if (answer) {
          setBodyDraft({
            sex: answer.sex,
            age: answer.age !== null ? String(answer.age) : "",
            heightCm: answer.heightCm !== null ? String(answer.heightCm) : "",
            weightKg: answer.weightKg !== null ? String(answer.weightKg) : "",
            targetWeightKg: answer.targetWeightKg !== null ? String(answer.targetWeightKg) : "",
          })
        }
        setState("ready")
      })
      .catch(() => {
        if (!cancelled) setState("error")
      })
    return () => {
      cancelled = true
    }
  }, [api])

  // Client-side pre-validation mirrors the wire schema for inline feedback;
  // the server re-validates authoritatively either way.
  // Owner report 2026-08-30: a starred field could be emptied and "saved"
  // without complaint — the patch builder just skipped it. A starred field
  // that has a stored value now BLOCKS the save when cleared.
  const dobCleared =
    profile !== null &&
    profile.personalInfo.dateOfBirth !== null &&
    personalDraft.dateOfBirth.trim() === ""

  const personalPatch = profile ? personalPatchFrom(profile, personalDraft) : null
  const personalValidation = personalPatch ? PersonalInfoPatchSchema.safeParse(personalPatch) : null
  const phoneInvalid =
    personalValidation !== null &&
    !personalValidation.success &&
    personalValidation.error.issues.some(i => i.path[0] === "phone")
  const dobInvalid =
    personalValidation !== null &&
    !personalValidation.success &&
    personalValidation.error.issues.some(i => i.path[0] === "dateOfBirth")

  async function handlePersonalSave() {
    if (!profile || !personalPatch || !personalValidation?.success) return
    setPersonalState("saving")
    try {
      const updated = await api.patchProfile({ personalInfo: personalPatch })
      setProfile(updated)
      setPersonalDraft(personalDraftFrom(updated))
      setPersonalState("saved")
    } catch {
      setPersonalState("error")
    }
  }

  async function handleBodySave() {
    if (!profile || !step4 || !bodyDraft || bodyDraftInvalid(bodyDraft)) return
    setBodyState("saving")
    const prevKcal = profile.calorieTarget.kcal
    try {
      // Merge over the stored answer so activity (and any future fields)
      // survive; sex/age/height/weight are now required inputs (owner
      // decision 2026-08-28 removed prefer-not-to-answer), so the refusal
      // list is always empty on this write path.
      const answer = {
        ...step4,
        sex: bodyDraft.sex,
        age: numOrNull(bodyDraft.age),
        heightCm: numOrNull(bodyDraft.heightCm),
        weightKg: numOrNull(bodyDraft.weightKg),
        targetWeightKg: numOrNull(bodyDraft.targetWeightKg),
        preferNotToAnswer: [],
      }
      await api.putOnboardingStep(4, { step: 4, answer } as never)
      setStep4(answer as Step4Answer)
      // The step-4 PUT recomputes targets server-side — re-read the profile
      // to learn the new target, then sync the plan (ADR-033).
      const updated = await api.getProfile()
      setProfile(updated)
      setBodyState("saved")
      const toKcal = updated.calorieTarget.kcal
      setBodyNotice(
        prevKcal !== null && toKcal !== null && prevKcal !== toKcal
          ? { fromKcal: prevKcal, toKcal }
          : null
      )
    } catch {
      setBodyState("error")
    }
  }

  async function handlePhotoSelected(file: File) {
    if (!profile) return
    setPhotoState("uploading")
    try {
      const processed = await processAvatarFile(file)
      if ("error" in processed) {
        setPhotoState("error")
        return
      }
      const { uploadUrl, photoKey } = await api.createProfilePhotoUpload()
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": "image/jpeg" },
        body: processed.blob,
      })
      if (!putRes.ok) {
        setPhotoState("error")
        return
      }
      const updated = await api.patchProfile({ personalInfo: { photoKey } })
      setProfile(updated)
      setPhotoState("idle")
    } catch {
      setPhotoState("error")
    }
  }

  async function handlePhotoRemove() {
    if (!profile) return
    setPhotoState("uploading")
    try {
      const updated = await api.patchProfile({ personalInfo: { photoKey: null } })
      setProfile(updated)
      setPhotoState("idle")
    } catch {
      setPhotoState("error")
    }
  }

  if (state === "loading") return <LoadingState />
  if (state === "error" || !profile) return <ErrorState onRetry={() => window.location.reload()} />

  const personal = profile.personalInfo
  const profileIncomplete = personal.firstName === null || personal.dateOfBirth === null

  return (
    <section
      aria-labelledby="profile-heading"
      className="mx-auto flex w-full max-w-2xl flex-col gap-6"
    >
      <MoreSubpageHeader title={t("moreProfile.profile.title")} headingId="profile-heading" />

      {profileIncomplete && (
        <div className="flex flex-col gap-1 rounded-bb-md border border-amber-300 bg-amber-50 p-4 text-amber-900">
          <p className="text-sm font-semibold">{t("moreProfile.profile.completeBannerTitle")}</p>
          <p className="text-sm">{t("moreProfile.profile.completeBannerBody")}</p>
        </div>
      )}

      {/* ------------------------------------------------ Personal information */}
      <div className="flex flex-col gap-4 rounded-bb-md border border-line bg-paper p-5">
        <h2 className="text-lg font-semibold text-ink">{t("moreProfile.profile.personalTitle")}</h2>

        <div className="flex items-center gap-4">
          {personal.photoUrl ? (
            <img
              src={personal.photoUrl}
              alt={t("moreProfile.profile.photoAlt")}
              className="h-16 w-16 rounded-full border border-line object-cover"
            />
          ) : (
            <span
              aria-hidden="true"
              className="flex h-16 w-16 items-center justify-center rounded-full bg-green/15 text-xl font-bold text-green"
            >
              {avatarInitials(personal.firstName, personal.familyName, personal.email)}
            </span>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              aria-label={
                personal.photoUrl
                  ? t("moreProfile.profile.photoReplace")
                  : t("moreProfile.profile.photoUpload")
              }
              onChange={e => {
                const file = e.target.files?.[0]
                if (file) void handlePhotoSelected(file)
                e.target.value = ""
              }}
            />
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={photoState === "uploading"}
              onClick={() => fileInputRef.current?.click()}
            >
              {photoState === "uploading"
                ? t("moreProfile.profile.photoUploading")
                : personal.photoUrl
                  ? t("moreProfile.profile.photoReplace")
                  : t("moreProfile.profile.photoUpload")}
            </Button>
            {personal.photoUrl && (
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                disabled={photoState === "uploading"}
                onClick={() => void handlePhotoRemove()}
              >
                {t("moreProfile.profile.photoRemove")}
              </Button>
            )}
            {photoState === "error" && (
              <span className="text-sm text-destructive">
                {t("moreProfile.profile.photoError")}
              </span>
            )}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t("moreProfile.profile.firstNameLabel")}
            htmlFor="profile-first-name"
            required={false}
          >
            <Input
              id="profile-first-name"
              autoComplete="given-name"
              value={personalDraft.firstName}
              onChange={e => setPersonalDraft(d => ({ ...d, firstName: e.target.value }))}
            />
          </Field>
          <Field
            label={t("moreProfile.profile.familyNameLabel")}
            htmlFor="profile-family-name"
            required={false}
          >
            <Input
              id="profile-family-name"
              autoComplete="family-name"
              value={personalDraft.familyName}
              onChange={e => setPersonalDraft(d => ({ ...d, familyName: e.target.value }))}
            />
          </Field>
        </div>

        <Field label={t("moreProfile.profile.dobLabel")} htmlFor="profile-dob">
          <Input
            id="profile-dob"
            type="date"
            autoComplete="bday"
            value={personalDraft.dateOfBirth}
            onChange={e => setPersonalDraft(d => ({ ...d, dateOfBirth: e.target.value }))}
          />
          {dobInvalid && (
            <p className="text-xs text-destructive">{t("onboarding.step2.dobInvalid")}</p>
          )}
          {dobCleared && (
            <p className="text-xs text-destructive">
              {t("moreProfile.profile.requiredFieldError")}
            </p>
          )}
        </Field>

        <Field label={t("moreProfile.profile.emailLabel")} htmlFor="profile-email" required={false}>
          <Input id="profile-email" value={personal.email} readOnly disabled />
          <p className="text-xs text-ink-2">{t("moreProfile.profile.emailReadOnlyHint")}</p>
        </Field>

        <Field label={t("moreProfile.profile.phoneLabel")} htmlFor="profile-phone" required={false}>
          <Input
            id="profile-phone"
            type="tel"
            autoComplete="tel"
            placeholder={t("moreProfile.profile.phoneOptionalHint")}
            value={personalDraft.phone}
            onChange={e => setPersonalDraft(d => ({ ...d, phone: e.target.value }))}
          />
          {phoneInvalid && (
            <p className="text-xs text-destructive">{t("moreProfile.profile.phoneInvalid")}</p>
          )}
        </Field>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            className="min-h-11"
            onClick={() => void handlePersonalSave()}
            disabled={
              personalState === "saving" ||
              dobCleared ||
              personalPatch === null ||
              !personalValidation?.success
            }
          >
            {personalState === "saving"
              ? t("onboarding.shell.saving")
              : t("moreProfile.profile.saveButton")}
          </Button>
          {personalState === "saved" && (
            <span className="text-sm text-green">{t("moreProfile.profile.personalSaved")}</span>
          )}
          {personalState === "error" && (
            <span className="text-sm text-destructive">
              {t("moreProfile.profile.personalSaveError")}
            </span>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------- Body data */}
      {bodyDraft && (
        <div className="flex flex-col gap-4 rounded-bb-md border border-line bg-paper p-5">
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-semibold text-ink">{t("moreProfile.profile.bodyTitle")}</h2>
            <p className="text-sm text-ink-2">{t("moreProfile.profile.bodyIntro")}</p>
          </div>

          <div>
            <p className="mb-2 text-sm font-semibold text-ink">{t("onboarding.step4.sexLabel")}</p>
            <OptionGroup
              legend={t("onboarding.step4.sexLabel")}
              columns={2}
              value={bodyDraft.sex}
              onChange={v => setBodyDraft(d => (d ? { ...d, sex: v as "female" | "male" } : d))}
              options={[
                { value: "female", label: t("onboarding.step4.sexFemale") },
                { value: "male", label: t("onboarding.step4.sexMale") },
              ]}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("onboarding.step4.ageLabel")} htmlFor="body-age">
              <Input
                id="body-age"
                type="number"
                min={18}
                max={100}
                value={bodyDraft.age}
                onChange={e => setBodyDraft(d => (d ? { ...d, age: e.target.value } : d))}
              />
            </Field>
            <Field label={t("onboarding.step4.heightLabel")} htmlFor="body-height">
              <Input
                id="body-height"
                type="number"
                min={120}
                max={230}
                value={bodyDraft.heightCm}
                onChange={e => setBodyDraft(d => (d ? { ...d, heightCm: e.target.value } : d))}
              />
            </Field>
            <Field label={t("onboarding.step4.weightLabel")} htmlFor="body-weight">
              <Input
                id="body-weight"
                type="number"
                min={30}
                max={300}
                step="0.1"
                value={bodyDraft.weightKg}
                onChange={e => setBodyDraft(d => (d ? { ...d, weightKg: e.target.value } : d))}
              />
            </Field>
            <Field
              label={t("onboarding.step4.targetWeightLabel")}
              htmlFor="body-target-weight"
              required={false}
            >
              <Input
                id="body-target-weight"
                type="number"
                min={30}
                max={300}
                step="0.1"
                value={bodyDraft.targetWeightKg}
                onChange={e =>
                  setBodyDraft(d => (d ? { ...d, targetWeightKg: e.target.value } : d))
                }
              />
            </Field>
          </div>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              className="min-h-11"
              onClick={() => void handleBodySave()}
              disabled={bodyState === "saving" || bodyDraftInvalid(bodyDraft)}
            >
              {bodyState === "saving"
                ? t("onboarding.shell.saving")
                : t("moreProfile.profile.saveButton")}
            </Button>
            {bodyState === "saved" && (
              <span className="text-sm text-green">{t("moreProfile.profile.bodySaved")}</span>
            )}
            {bodyState === "error" && (
              <span className="text-sm text-destructive">
                {t("moreProfile.profile.bodySaveError")}
              </span>
            )}
            {bodyDraftInvalid(bodyDraft) && (
              <span className="text-sm text-ink-2">{t("moreProfile.profile.bodyInvalid")}</span>
            )}
          </div>
          {bodyState === "saved" && bodyNotice === null && (
            <p className="text-sm text-ink-2">{t("moreProfile.profile.bodyTargetUnchanged")}</p>
          )}
          {bodyNotice && (
            <div className="flex flex-col gap-2 rounded-bb-md border border-line bg-cream p-4">
              <p className="text-sm text-ink">
                {t("moreProfile.profile.bodyTargetKept", {
                  from: formatKcal(bodyNotice.fromKcal, locale),
                  to: formatKcal(bodyNotice.toKcal, locale),
                })}
              </p>
              {Math.abs(bodyNotice.toKcal - bodyNotice.fromKcal) >= REBUILD_OFFER_THRESHOLD_KCAL && (
                <>
                  <p className="text-sm text-ink-2">{t("moreProfile.profile.bodyRebuildOffer")}</p>
                  <Button
                    type="button"
                    variant="outline"
                    className="min-h-11 self-start"
                    onClick={() => {
                      const notice = bodyNotice
                      setBodyNotice(null)
                      startPlanSync({ fromKcal: notice.fromKcal, toKcal: notice.toKcal })
                    }}
                  >
                    {t("moreProfile.profile.bodyRebuildCta")}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      <PlanSyncBanner
        state={planSync}
        onRetry={() => startPlanSync({ fromKcal: null, toKcal: profile.calorieTarget.kcal })}
      />
    </section>
  )
}
